import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Badge,
  Menu,
  MenuItem
} from '@mui/material';
import {
  AdminPanelSettings,
  Group,
  Person,
  Security,
  Dashboard,
  Settings as SettingsIcon,
  Add as AddIcon,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Refresh,
  TrendingUp,
  People,
  Folder,
  Shield,
  History,
  Email,
  Lock,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Help as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  Sort as SortIcon,
  Analytics as AnalyticsIcon,
  RocketLaunch as RocketLaunchIcon,
  ArrowForward as ArrowForwardIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Hub as HubIcon
} from '@mui/icons-material';

import ActivityLogs from './ActivityLogs';
import UserGroupsManagement from './UserGroupsManagement';
import UsersManagement from './UsersManagement';
import ProjectsManagement from './ProjectsManagement';
import InvitationManagement from './InvitationManagement';
import rbacApi from '../../api/rbacApi';
import Sidebar from '../Sidebar';
import { dsVar } from '../../theme/tokens';

// Organization-level Access Control - Project-level permissions are handled in the Projects tab

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`access-control-tabpanel-${index}`}
      aria-labelledby={`access-control-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface UserPermissions {
  // Organization-level permissions only
  permissions: {
    canManageUsers: boolean;
    canInviteCrossGroupUsers: boolean;
    hasAccess: boolean;
    isOrgAdmin: boolean;
    isGroupAdmin: boolean;
    canAddCrossGroupUsers: boolean;
    canManageProjectUsers: boolean;
    canEditProjectSettings: boolean;
  };
  
  // Basic user data
  userGroups?: any[];
  projectAccess?: any[];
  
  // Metadata
  userId?: string;
  organisationId?: string;
  lastUpdated?: string;
}

const UserRolesPermissions: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLDivElement>) => {
    setUserMenuAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleSignOut = () => {
    localStorage.clear();
    handleUserMenuClose();
    navigate('/login');
  };

  // Load real data from backend
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const orgId = localStorage.getItem('org_id');
      const userEmail = localStorage.getItem('user_email');
      
      if (!orgId || !userEmail) {
        throw new Error('Missing authentication data. Please log in again.');
      }
      
      // Fetch current user data
      let currentUser;
      try {
        currentUser = await (rbacApi as any).getCurrentUser(userEmail);
        if (currentUser && currentUser.user) {
          currentUser = currentUser.user;
        }
      } catch (error) {
        console.error('❌ getCurrentUser failed:', error);
      }

      // Fetch user permissions
      let userPerms: any;
      try {
        userPerms = await rbacApi.getUserLevelPermissions(userEmail);
        
        if (userPerms && userPerms.permissions) {
          // Fetch user groups
          try {
            const userGroupsResponse = await rbacApi.getUserGroups(userEmail);
            userPerms.userGroups = userGroupsResponse?.groups || [];
          } catch (dataError) {
            userPerms.userGroups = [];
          }
          userPerms.projectAccess = [];
          setUserPermissions(userPerms as UserPermissions);
        }
      } catch (error) {
        console.error('❌ getUserPermissions failed:', error);
        setError('Failed to load user permissions.');
      }

      // Fetch Stats
      try {
        const [usersRes, groupsRes, projectsRes] = await Promise.all([
          rbacApi.getUsers(),
          rbacApi.getGroups(),
          rbacApi.getProjects()
        ]);

        const totalUsers = Array.isArray(usersRes) ? usersRes.length : (usersRes?.users?.length || 0);
        const totalGroups = Array.isArray(groupsRes) ? groupsRes.length : (groupsRes?.groups?.length || 0);
        const totalProjects = Array.isArray(projectsRes) ? projectsRes.length : (projectsRes?.projects?.length || 0);

        setStats({
          totalUsers,
          totalGroups,
          totalProjects,
          activeUsers: totalUsers,
          pendingInvites: 0,
          recentActivity: 0
        });
      } catch (error) {
        console.error('❌ Stats fetch failed:', error);
      }

      if (currentUser) {
        setUser({
          ...currentUser,
          name: currentUser.userName || currentUser.name || userEmail.split('@')[0],
          role: rbacApi.mapUserRole(currentUser.role || currentUser.userRole) || 'user'
        });
      }
      
    } catch (err: any) {
      console.error('Error loading access control data:', err);
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getVisibleTabs = () => {
    if (!userPermissions) return [];
    
    const tabs = [];
    // Always show Users as first tab if permitted
    if (userPermissions.permissions.canManageUsers || userPermissions.permissions.isOrgAdmin) {
      tabs.push({ index: 0, label: 'Users', icon: <People /> });
    }
    if (userPermissions.permissions.canManageUsers || userPermissions.permissions.isGroupAdmin) {
      tabs.push({ index: 1, label: 'User Groups', icon: <Group /> });
    }
    if (userPermissions.permissions.canEditProjectSettings || userPermissions.permissions.isOrgAdmin) {
      tabs.push({ index: 2, label: 'Projects', icon: <Folder /> });
    }
    if (userPermissions.permissions.canInviteCrossGroupUsers || userPermissions.permissions.isOrgAdmin) {
      tabs.push({ index: 3, label: 'Invitations', icon: <Email /> });
    }
    // Always show Activity Logs last
    tabs.push({ index: 4, label: 'Activity Logs', icon: <History /> });
    
    return tabs;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor={dsVar('page')}>
        <CircularProgress sx={{ color: dsVar('accent-fg') }} />
      </Box>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-page font-sans text-fg selection:bg-accent selection:text-on-accent">
      <Sidebar />

      <main className="flex-1 flex flex-col overflow-hidden bg-page">
        {/* Header */}
        <header className="h-16 border-b border-line/50 bg-page/80 backdrop-blur-md px-8 flex items-center justify-between sticky top-0 z-10">
          <div className="flex-1 max-w-2xl text-left">
            <div className="relative group flex items-center justify-start h-full">
              <SearchIcon className="absolute left-3 text-fg-muted group-focus-within:text-accent-fg transition-colors" />
              <input
                className="w-full bg-surface border-none rounded-lg py-2 pl-10 pr-4 text-sm focus:ring-1 focus:ring-accent-fg placeholder:text-fg-muted text-fg outline-none"
                placeholder="Search users, groups, or projects..."
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-4 ml-8">
            <IconButton sx={{ color: dsVar('fg-2'), '&:hover': { color: dsVar('accent-fg'), bgcolor: 'rgba(255, 222, 89, 0.1)' } }}>
              <NotificationsIcon />
            </IconButton>
            <IconButton sx={{ color: dsVar('fg-2'), '&:hover': { color: dsVar('accent-fg'), bgcolor: 'rgba(255, 222, 89, 0.1)' } }}>
              <HelpIcon />
            </IconButton>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          {error && (
            <Alert severity="error" className="mb-6 bg-red-900/20 text-red-100 border border-red-900/50" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div className="mb-10 p-6 rounded-2xl bg-gradient-to-r from-surface to-[#1a365d] border border-line flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center border border-accent-fg/20 shadow-lg shadow-accent/5">
                <Security sx={{ fontSize: 32, color: dsVar('accent-fg') }} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-fg mb-1">Access Control & Security</h2>
                <p className="text-fg-2">Manage organization-level roles, user groups, and platform permissions.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="text-right px-6 border-r border-line">
                <p className="text-xs text-fg-muted uppercase tracking-widest mb-1 font-bold">Your Role</p>
                <Chip 
                  label={user?.role?.replace('_', ' ').toUpperCase() || 'USER'} 
                  size="small" 
                  sx={{ 
                    bgcolor: dsVar('accent'), 
                    color: dsVar('on-accent'), 
                    fontWeight: 800,
                    borderRadius: '6px',
                    fontSize: '0.7rem'
                  }} 
                />
              </div>
              <div className="text-right px-2">
                <p className="text-xs text-fg-muted uppercase tracking-widest mb-1 font-bold">Org ID</p>
                <p className="text-sm font-mono text-accent-fg">{localStorage.getItem('org_id')}</p>
              </div>
            </div>
          </div>

          <Box sx={{ borderBottom: 1, borderColor: dsVar('line'), mb: 4 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTabs-indicator': {
                  bgcolor: dsVar('accent'),
                  height: 3,
                  borderRadius: '3px 3px 0 0'
                },
                '& .MuiTab-root': {
                  color: dsVar('fg-2'),
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  minWidth: 120,
                  transition: 'all 0.2s',
                  '&:hover': {
                    color: dsVar('accent-fg'),
                    bgcolor: 'rgba(255, 222, 89, 0.05)'
                  },
                  '&.Mui-selected': {
                    color: dsVar('accent-fg')
                  }
                }
              }}
            >
              {getVisibleTabs().map((tab) => (
                <Tab key={tab.index} icon={tab.icon} iconPosition="start" label={tab.label} value={tab.index} />
              ))}
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <UsersManagement 
              canManageUsers={userPermissions?.permissions?.canManageUsers}
              canDeleteUsers={userPermissions?.permissions?.isOrgAdmin}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <UserGroupsManagement 
              canCreateGroups={userPermissions?.permissions?.canManageUsers}
              canManageGroups={userPermissions?.permissions?.canManageUsers}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <ProjectsManagement 
              canManageProjects={userPermissions?.permissions?.canManageProjectUsers}
              canCrossGroupAccess={userPermissions?.permissions?.canInviteCrossGroupUsers}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <InvitationManagement 
              canSendInvitations={userPermissions?.permissions?.canManageUsers}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={4}>
            <ActivityLogs />
          </TabPanel>
        </div>
      </main>
    </div>
  );
};

export default UserRolesPermissions;